import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { API_BASE } from '../lib/apiBase';
import { Button } from './ui/button';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from './ui/dialog';
import {
    Popover,
    PopoverContent,
    PopoverTrigger,
} from './ui/popover';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import { Loader2, MoreHorizontal, Check, Copy, XCircle, Clock, ExternalLink } from 'lucide-react';

const SCOPE_LABELS = {
    'supplier-confirmation': 'Supplier Confirmation',
    'dispatch-information': 'Dispatch Information',
};

// Default snooze: end of today + 1 day (i.e. "tomorrow 23:59 UTC").
// User-picked date snoozes are normalized to end-of-day on the backend.
const defaultSnoozeDate = () => {
    const d = new Date();
    d.setDate(d.getDate() + 1);
    return d.toISOString().slice(0, 10);
};

// Fetch the list of unresolved needs-attention items for a scope.
// Refreshed whenever the panel opens or an action completes.
export function NeedsAttentionResolvePanel({ scope, onResolved, trigger }) {
    const [open, setOpen] = useState(false);
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(false);
    const [busyOrderId, setBusyOrderId] = useState(null);

    // Dialog state for dismiss + snooze actions
    const [dismissTarget, setDismissTarget] = useState(null); // {order_id, order_number}
    const [dismissReason, setDismissReason] = useState('');
    const [snoozeTarget, setSnoozeTarget] = useState(null);
    const [snoozeUntil, setSnoozeUntil] = useState(defaultSnoozeDate());

    const fetchItems = useCallback(async () => {
        setLoading(true);
        try {
            const token = localStorage.getItem('token');
            const res = await axios.get(
                `${API_BASE}/api/admin/needs-attention/${scope}`,
                { headers: { Authorization: `Bearer ${token}` } },
            );
            setItems(res.data?.items || []);
        } catch (err) {
            console.error('Failed to fetch needs-attention items', err);
            toast.error('Failed to load items');
        } finally {
            setLoading(false);
        }
    }, [scope]);

    useEffect(() => {
        if (open) fetchItems();
    }, [open, fetchItems]);

    const runAction = async (orderId, action, body) => {
        setBusyOrderId(orderId);
        try {
            const token = localStorage.getItem('token');
            await axios.post(
                `${API_BASE}/api/admin/needs-attention/${scope}/${orderId}/${action}`,
                body || {},
                { headers: { Authorization: `Bearer ${token}` } },
            );
            const label = {
                approve: 'Approved',
                'mark-duplicate': 'Marked as duplicate',
                dismiss: 'Dismissed',
                snooze: 'Snoozed',
            }[action] || 'Resolved';
            toast.success(`${label}`);
            await fetchItems();
            if (onResolved) onResolved();
        } catch (err) {
            const detail = err.response?.data?.detail || err.message || 'Action failed';
            toast.error(detail);
        } finally {
            setBusyOrderId(null);
        }
    };

    const submitDismiss = async () => {
        if (!dismissTarget || !dismissReason.trim()) {
            toast.error('Reason is required');
            return;
        }
        await runAction(dismissTarget.order_id, 'dismiss', { reason: dismissReason.trim() });
        setDismissTarget(null);
        setDismissReason('');
    };

    const submitSnooze = async () => {
        if (!snoozeTarget || !snoozeUntil) {
            toast.error('Pick a date');
            return;
        }
        await runAction(snoozeTarget.order_id, 'snooze', { snooze_until: snoozeUntil });
        setSnoozeTarget(null);
        setSnoozeUntil(defaultSnoozeDate());
    };

    const scopeLabel = SCOPE_LABELS[scope] || scope;

    return (
        <>
            <Popover open={open} onOpenChange={setOpen}>
                <PopoverTrigger asChild>
                    {trigger || (
                        <Button
                            size="sm"
                            variant="ghost"
                            className="h-6 w-6 p-0"
                            data-testid={`needs-attention-resolve-${scope}`}
                        >
                            <MoreHorizontal className="h-4 w-4" />
                        </Button>
                    )}
                </PopoverTrigger>
                <PopoverContent
                    className="w-[520px] p-0 max-h-[70vh] overflow-hidden flex flex-col"
                    align="end"
                    data-testid={`needs-attention-panel-${scope}`}
                >
                    <div className="px-4 py-3 border-b bg-slate-50">
                        <div className="text-sm font-semibold text-slate-800">
                            Resolve · {scopeLabel}
                        </div>
                        <div className="text-xs text-slate-500 mt-0.5">
                            Approve, mark duplicate, dismiss or snooze each item.
                        </div>
                    </div>

                    <div className="overflow-y-auto flex-1">
                        {loading && (
                            <div className="flex items-center justify-center py-6 text-slate-500 text-sm">
                                <Loader2 className="h-4 w-4 animate-spin mr-2" />
                                Loading…
                            </div>
                        )}

                        {!loading && items.length === 0 && (
                            <div className="text-center py-8 text-sm text-slate-500">
                                No items to resolve. All caught up.
                            </div>
                        )}

                        {!loading && items.map((item) => (
                            <div
                                key={item.order_id}
                                className="px-4 py-3 border-b last:border-b-0 hover:bg-slate-50"
                                data-testid={`needs-attention-item-${item.order_id}`}
                            >
                                <div className="flex items-start justify-between gap-2 mb-2">
                                    <div className="flex-1 min-w-0">
                                        <a
                                            href={`/orders/${item.order_id}`}
                                            className="text-sm font-semibold text-indigo-700 hover:underline flex items-center gap-1"
                                        >
                                            #{String(item.order_number || '').padStart(6, '0')}
                                            <ExternalLink className="h-3 w-3" />
                                        </a>
                                        <div className="text-xs text-slate-600 truncate">
                                            {item.order_title || item.catalogue_number || '—'}
                                        </div>
                                        {item.changed_fields && item.changed_fields.length > 0 && (
                                            <div className="text-[11px] text-amber-700 mt-1">
                                                Changed: {item.changed_fields.join(', ')}
                                            </div>
                                        )}
                                        {scope === 'dispatch-information' && item.snapshot && (
                                            <div className="text-[11px] text-slate-500 mt-1">
                                                {item.snapshot.dispatch_delivery_count}× delivery ·{' '}
                                                {item.snapshot.dispatch_total_qty || 0} units
                                                {item.snapshot.dispatch_planned_arrival_date
                                                    ? ` · arrive ${item.snapshot.dispatch_planned_arrival_date}`
                                                    : ''}
                                            </div>
                                        )}
                                    </div>
                                </div>
                                <div className="flex flex-wrap items-center gap-1.5">
                                    <Button
                                        size="sm"
                                        variant="outline"
                                        className="h-7 px-2 text-xs border-emerald-300 text-emerald-700 hover:bg-emerald-50"
                                        onClick={() => runAction(item.order_id, 'approve')}
                                        disabled={busyOrderId === item.order_id}
                                        data-testid={`resolve-approve-${item.order_id}`}
                                    >
                                        <Check className="h-3 w-3 mr-1" />
                                        Approve
                                    </Button>
                                    <Button
                                        size="sm"
                                        variant="outline"
                                        className="h-7 px-2 text-xs border-slate-300 text-slate-700 hover:bg-slate-100"
                                        onClick={() => runAction(item.order_id, 'mark-duplicate')}
                                        disabled={busyOrderId === item.order_id}
                                        data-testid={`resolve-duplicate-${item.order_id}`}
                                    >
                                        <Copy className="h-3 w-3 mr-1" />
                                        Duplicate
                                    </Button>
                                    <Button
                                        size="sm"
                                        variant="outline"
                                        className="h-7 px-2 text-xs border-red-300 text-red-700 hover:bg-red-50"
                                        onClick={() => {
                                            setDismissTarget(item);
                                            setDismissReason('');
                                        }}
                                        disabled={busyOrderId === item.order_id}
                                        data-testid={`resolve-dismiss-${item.order_id}`}
                                    >
                                        <XCircle className="h-3 w-3 mr-1" />
                                        Dismiss
                                    </Button>
                                    <Button
                                        size="sm"
                                        variant="outline"
                                        className="h-7 px-2 text-xs border-blue-300 text-blue-700 hover:bg-blue-50"
                                        onClick={() => {
                                            setSnoozeTarget(item);
                                            setSnoozeUntil(defaultSnoozeDate());
                                        }}
                                        disabled={busyOrderId === item.order_id}
                                        data-testid={`resolve-snooze-${item.order_id}`}
                                    >
                                        <Clock className="h-3 w-3 mr-1" />
                                        Snooze
                                    </Button>
                                </div>
                            </div>
                        ))}
                    </div>
                </PopoverContent>
            </Popover>

            {/* Dismiss dialog */}
            <Dialog
                open={!!dismissTarget}
                onOpenChange={(o) => {
                    if (!o) {
                        setDismissTarget(null);
                        setDismissReason('');
                    }
                }}
            >
                <DialogContent data-testid="dismiss-dialog">
                    <DialogHeader>
                        <DialogTitle>Dismiss with reason</DialogTitle>
                        <DialogDescription>
                            Order #{String(dismissTarget?.order_number || '').padStart(6, '0')} — a free-text reason is required for audit.
                        </DialogDescription>
                    </DialogHeader>
                    <div className="py-2">
                        <Label htmlFor="dismiss-reason" className="text-xs">Reason</Label>
                        <Textarea
                            id="dismiss-reason"
                            data-testid="dismiss-reason-input"
                            value={dismissReason}
                            onChange={(e) => setDismissReason(e.target.value)}
                            placeholder="Why is this item being dismissed?"
                            className="mt-1"
                            rows={3}
                        />
                    </div>
                    <DialogFooter>
                        <Button
                            variant="outline"
                            onClick={() => {
                                setDismissTarget(null);
                                setDismissReason('');
                            }}
                            data-testid="dismiss-cancel"
                        >
                            Cancel
                        </Button>
                        <Button
                            onClick={submitDismiss}
                            disabled={!dismissReason.trim() || busyOrderId === dismissTarget?.order_id}
                            data-testid="dismiss-submit"
                            className="bg-red-600 hover:bg-red-700 text-white"
                        >
                            Dismiss
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Snooze dialog */}
            <Dialog
                open={!!snoozeTarget}
                onOpenChange={(o) => {
                    if (!o) {
                        setSnoozeTarget(null);
                        setSnoozeUntil(defaultSnoozeDate());
                    }
                }}
            >
                <DialogContent data-testid="snooze-dialog">
                    <DialogHeader>
                        <DialogTitle>Snooze until</DialogTitle>
                        <DialogDescription>
                            Order #{String(snoozeTarget?.order_number || '').padStart(6, '0')} — will hide from Needs Attention until the chosen date.
                        </DialogDescription>
                    </DialogHeader>
                    <div className="py-2">
                        <Label htmlFor="snooze-until" className="text-xs">Snooze until</Label>
                        <Input
                            id="snooze-until"
                            data-testid="snooze-until-input"
                            type="date"
                            value={snoozeUntil}
                            min={new Date().toISOString().slice(0, 10)}
                            onChange={(e) => setSnoozeUntil(e.target.value)}
                            className="mt-1"
                        />
                        <div className="text-[11px] text-slate-500 mt-2">
                            Default is tomorrow. Item re-appears at end of the chosen day.
                        </div>
                    </div>
                    <DialogFooter>
                        <Button
                            variant="outline"
                            onClick={() => {
                                setSnoozeTarget(null);
                                setSnoozeUntil(defaultSnoozeDate());
                            }}
                            data-testid="snooze-cancel"
                        >
                            Cancel
                        </Button>
                        <Button
                            onClick={submitSnooze}
                            disabled={!snoozeUntil || busyOrderId === snoozeTarget?.order_id}
                            data-testid="snooze-submit"
                            className="bg-blue-600 hover:bg-blue-700 text-white"
                        >
                            Snooze
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </>
    );
}

export default NeedsAttentionResolvePanel;
